import { useForm } from 'react-hook-form';
import { Icon } from '@iconify/react';
import { Box, Grid, Skeleton, Tooltip, Typography } from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { capitalizeFirstLetter } from '../../../../../../../../utils/commonFunctions';
import { useAppSelector } from '../../../../../../../../app/redux/hooks';
import { useVehicleCardHooks } from './hooks/VehicleCardHooks';
import carImg from '../../../../../../../../app/assets/images/vehicles/car.png';
import bikeImg from '../../../../../../../../app/assets/images/vehicles/bike.png';
import busImg from '../../../../../../../../app/assets/images/vehicles/bus.png';
import truckImg from '../../../../../../../../app/assets/images/vehicles/truck.png';
import scootyImg from '../../../../../../../../app/assets/images/vehicles/scooty.png';
import comfyImg from '../../../../../../../../app/assets/images/vehicles/comfy.png';
import CustomButton from '../../../../../../../components/buttons/CustomButton';
import CustomTextField from '../../../../../../../components/customized/customtextfield/CustomTextField';
import LoginRedirect from '../../../../../../../components/loginredirect/LoginRedirect';
import InfiniteScroll from '../../../../../../../components/infinitescroll/InfiniteScroll';
import VehicleDetails from './VehicleDetails';
import './VehicleCard.scss';

interface Vehicle {
  id: number;
  vehicleNumber: string;
  vehicleType: string;
  driverName: string;
  fuelType: string;
  vehicleCategory: string;
  transmissionType: string;
  vehicleMake: string;
  seatingCapacity: number | null;
  numberOfAxles: number | null;
  primaryDriver?: string | null;
  secondaryDrivers?: string[] | [];
}

const VehicleCard = ({ onScroll, setApi, clearState }: any) => {
  const vehicleList = useAppSelector(state => state.vehicleList);
  const { control } = useForm();

  const {
    handleFilterChange,
    handleOpenVehicle,
    inputRef,
    filter,
    vehicleDatas,
    isScroll,
    setPageno,
    pageno,
    isOpen,
    setIsOpen,
    vehicleDetails
  } = useVehicleCardHooks({ setApi, clearState, vehicleList });

  return (
    <>
      <Box
        className='vehicle-card-container'
        onScroll={() => {
          const { scrollTop, scrollHeight, clientHeight } = inputRef.current;
          const data = { scrollTop, scrollHeight, clientHeight };
          !vehicleList.isLoading && onScroll(data);
        }}
        ref={inputRef}
      >
        <Box sx={{ display: 'flex', justifyContent: 'right', width: '100%' }}>
          <Box sx={{ width: 200 }}>
            <CustomTextField
              name='search'
              control={control}
              id='filter-input'
              placeholder='Search...'
              value={filter}
              variant='standard'
              icon={<SearchOutlinedIcon color='primary' />}
              onChangeCallback={(e: any) => handleFilterChange(e)}
            />
          </Box>
        </Box>
        {vehicleList.isLoading && vehicleDatas.length === 0 ? (
          <Box>
            <Grid container spacing={1} className='vehicle-component'>
              {[...Array(4)].map((items: Vehicle, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <Box className='vehicle-card'>
                    <Box>
                      <Skeleton variant='rectangular' height={'127px'} />
                    </Box>
                    <Box className='vehicle-details'>
                      <Box className=''>
                        <Typography className='vehicle-no'>
                          <Skeleton variant='text' width={200} height={40} />
                        </Typography>
                        <Typography className='vehicle-catorgy'>
                        </Typography>
                      </Box>
                      <Typography className='driver-name'>
                        <Skeleton variant='text' width={140} height={40} />
                      </Typography>
                    </Box>
                    <Box className='vehicle-card-footer'>
                      <Box className='footer-content'>
                        <Skeleton variant='text' width={20} />
                        <Typography className={`content `}>
                          <Skeleton variant='text' width={40} />
                        </Typography>
                      </Box>
                      <Box className='footer-content'>
                        <Skeleton variant='text' width={20} />

                        <Typography className={`content `}>
                          <Skeleton variant='text' width={40} />
                        </Typography>
                      </Box>
                      <Box className='footer-content'>
                        <Skeleton variant='text' width={20} />

                        <Typography className='content'>
                          <Skeleton variant='text' width={40} />
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : vehicleList?.data?.length === 0 ? (
          <Box className='no-vehicle'>
            <Typography className='text'>No Vehicles Found</Typography>
          </Box>
        ) : (
          <Grid container spacing={1} className='vehicle-component'>
            {vehicleDatas.map((items: Vehicle, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={index}
                onClick={() => handleOpenVehicle(items)}
              >
                <Box className='vehicle-card'>
                  <Box className='vehicle-img-box'>
                    <Box
                      component={'img'}
                      src={
                        items.vehicleType === 'truck'
                          ? truckImg
                          : items.vehicleType === 'bike'
                          ? bikeImg
                          : items.vehicleType === 'bus' || items?.vehicleType === 'coach'
                          ? busImg
                          : items.vehicleType === 'scooter'
                          ? scootyImg
                          : items.vehicleType === 'comfy'
                          ? comfyImg
                          : carImg
                      }
                      className='vehicle-img'
                    />
                  </Box>
                  <Box className='vehicle-details'>
                    <Box className='info'>
                      <Typography className='vehicle-no'>
                        {items?.vehicleNumber.toUpperCase()}
                      </Typography>
                    </Box>
                    <Box className='info'>
                      <Typography className='vehicle-catorgy'>
                        {capitalizeFirstLetter(items?.vehicleMake)}
                      </Typography>
                    </Box>
                    {/* <Tooltip title={capitalizeFirstLetter(items?.primaryDriver ?? 'NA')}>
                      <Typography className='driver-name'>
                        {capitalizeFirstLetter(items?.primaryDriver ?? 'NA')}
                      </Typography>
                    </Tooltip> */}
                  </Box>
                  <Box className='vehicle-card-footer'>
                    <Box className='footer-content'>
                      <Icon className='icon' icon='lucide-lab:gearbox' />
                      <Typography
                        className={`content ${
                          items?.transmissionType?.length > 6 && 'over-length'
                        }`}
                      >
                        {capitalizeFirstLetter(items?.transmissionType)}
                      </Typography>
                    </Box>
                    <Box className='footer-content'>
                      <Icon className='icon' icon='mdi:fuel-pump' />
                      <Typography
                        className={`content ${
                          items?.fuelType?.length > 6 && 'over-length'
                        }`}
                      >
                        {Boolean(items?.fuelType)
                          ? capitalizeFirstLetter(items?.fuelType)
                          : '-'}
                      </Typography>
                    </Box>
                    <Box className='footer-content'>
                      <Icon
                        className='icon'
                        icon={
                          items.numberOfAxles === null
                            ? 'mdi:seat-passenger'
                            : 'mingcute:four-wheel-drive-fill'
                        }
                      />
                      <Typography className='content'>
                        {items?.seatingCapacity !== null
                          ? items?.seatingCapacity
                          : items?.numberOfAxles !== null
                          ? items?.numberOfAxles
                          : '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
        {vehicleList.isLoading && isScroll && vehicleList?.data?.length > 0 && (
          <Box className='loading-vehicle'>Loading.....</Box>
        )}
        {vehicleDatas.length >= 12 && (
          <CustomButton
            loading={vehicleList.isLoading}
            className='saveChanges'
            category='Load More'
            onClick={() => setPageno(pageno + 1)}
          />
        )}
        {isOpen && (
          <VehicleDetails
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            vehicleDetails={vehicleDetails}
          />
        )}
      </Box>
      <LoginRedirect />
    </>
  );
};

export default InfiniteScroll(VehicleCard);
